import React, { useCallback } from 'react';
import './ShareWidgetConfigModal.scss';
import LayoutManagerModal from 'layoutManagement/LayoutManagerModal';
import { WidgetType } from 'widgets/WidgetType';
import { LayoutManagerDictionary } from 'layoutManagement/LayoutManagerDictionary';
import { useServices } from 'app/hooks';
import {
    useDispatchUpdateWidgetConfiguration,
    useWidgetConfigState,
} from 'widgets/store/configuration/hooks';
import UIConfigurationDto from 'communication/types/uiConfiguration/UIConfigurationDto';
import { CapsuleContainerWidgetConfigState } from 'widgets/tiles/store/config/CapsuleContainerWidgetConfigState';
import { WidgetsService } from 'widgets/WidgetsService';

export interface ShareWidgetConfigModalProps {
    widgetType: WidgetType;
    isOpen: boolean;
    onActionCompleted: () => void;
}

export function ShareWidgetConfigModal(props: ShareWidgetConfigModalProps) {
    const { widgetType } = props;
    const { layoutService, widgetsService } = useServices();
    const widgetTitle = widgetsService.getWidgetTitle(widgetType);

    const config = useWidgetConfigState();
    const dispatchUpdateConfig = useDispatchUpdateWidgetConfiguration();

    const dictionary: Partial<LayoutManagerDictionary> = {
        title: `${widgetTitle} Layouts`,
        headerText: 'Widget Layout Manager',
        loadLayout: (layoutName) => `Apply ${layoutName} config`,
    };

    const onSaveAs = useCallback(
        async (name: string, id: number | null) => {
            const uiConfig: UIConfigurationDto = {
                id,
                layoutName: name,
                origin: widgetType,
                layout: JSON.stringify(config),
            };
            await layoutService.saveUiConfigDto(uiConfig);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [config, widgetType]
    );

    const onLoadLayout = useCallback(
        async (name: string, globalVersion?: boolean) => {
            const dto = await layoutService.fetchLayout(
                name,
                widgetType,
                !!globalVersion
            );
            const config: CapsuleContainerWidgetConfigState = JSON.parse(
                dto.layout
            );
            makeCapsuleIdsUnique(config, widgetsService);
            dispatchUpdateConfig(config, true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [widgetType]
    );

    return (
        <LayoutManagerModal
            className="ShareWidgetConfigModal"
            origin={widgetType}
            loadIcon={'check'}
            loadHoverIcon={'check'}
            showGlobalsWhenDuplicatedInUser={true}
            {...props}
            dictionary={dictionary}
            onSaveCurrent={onSaveAs}
            onLoadLayout={onLoadLayout}
        />
    );
}

export function makeCapsuleIdsUnique(
    config: CapsuleContainerWidgetConfigState,
    widgetsService: WidgetsService
) {
    if (!config?.tiles) {
        return;
    }

    for (const capsule of config.tiles) {
        const nextId = widgetsService.getNextWidgetId(capsule.widgetType);
        capsule.widgetKey = nextId;
    }
}
